import { useState } from "react";
import { Receipt, DollarSign, Clock, CheckCircle2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { StatCard } from "@/components/admin/StatCard";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { DataTable } from "@/components/admin/DataTable";
import type { DataTableColumn } from "@/components/admin/DataTable";
import { getInvoices } from "@/lib/admin/queries";
import { markAsPaid, refundPayment } from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const PLAN_PRICES: Record<string, number> = {
  individual: 9.99,
  strata: 18.99,
  property_manager: 28.99,
};

function getPlanKey(priceId: string): string {
  const lower = (priceId ?? "").toLowerCase();
  if (lower.includes("property_manager") || lower.includes("property-manager")) return "property_manager";
  if (lower.includes("strata")) return "strata";
  if (lower.includes("individual")) return "individual";
  return "unknown";
}

function getPlanLabel(priceId: string): string {
  const labels: Record<string, string> = {
    individual: "Individual",
    strata: "Strata Council",
    property_manager: "Property Manager",
  };
  return labels[getPlanKey(priceId)] ?? "Unknown";
}

export default function Invoices() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [refundTarget, setRefundTarget] = useState<Record<string, unknown> | null>(null);
  const [refundReason, setRefundReason] = useState("");

  const invoicesQuery = useQuery({
    queryKey: ["admin", "invoices", statusFilter, planFilter, dateFrom, dateTo],
    queryFn: () => getInvoices(),
  });

  const allInvoices: Record<string, unknown>[] = invoicesQuery.data?.data ?? [];

  // Client-side filter
  const filtered = allInvoices.filter((inv) => {
    if (statusFilter !== "all" && inv.status !== statusFilter) return false;
    if (planFilter !== "all" && getPlanKey((inv.price_id as string) ?? "") !== planFilter) return false;
    if (dateFrom && (inv.created_at as string) < dateFrom) return false;
    if (dateTo && (inv.created_at as string) > dateTo + "T23:59:59") return false;
    return true;
  });

  // Summary stats
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const thisMonth = allInvoices.filter((inv) => (inv.created_at as string) >= monthStart);
  const totalInvoiced = thisMonth.reduce((acc, inv) => acc + (PLAN_PRICES[getPlanKey((inv.price_id as string) ?? "")] ?? 0), 0);
  const totalCollected = thisMonth
    .filter((inv) => inv.status === "active")
    .reduce((acc, inv) => acc + (PLAN_PRICES[getPlanKey((inv.price_id as string) ?? "")] ?? 0), 0);
  const outstanding = thisMonth
    .filter((inv) => inv.status === "past_due")
    .reduce((acc, inv) => acc + (PLAN_PRICES[getPlanKey((inv.price_id as string) ?? "")] ?? 0), 0);

  const markPaidMutation = useMutation({
    mutationFn: (subId: string) => markAsPaid(subId, actorId),
    onSuccess: () => {
      toast.success("Marked as paid");
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
    },
    onError: () => toast.error("Failed to update"),
  });

  const refundMutation = useMutation({
    mutationFn: ({ customerId, reason }: { customerId: string; reason: string }) =>
      refundPayment(customerId, reason, actorId),
    onSuccess: () => {
      toast.success("Refund initiated");
      qc.invalidateQueries({ queryKey: ["admin", "invoices"] });
      setRefundTarget(null);
      setRefundReason("");
    },
    onError: () => toast.error("Failed to initiate refund"),
  });

  const columns: DataTableColumn<Record<string, unknown>>[] = [
    {
      key: "invoice_number",
      label: "Invoice #",
      render: (row) => (
        <span className="text-xs font-mono text-gray-500 whitespace-nowrap">
          {row.stripe_subscription_id
            ? (row.stripe_subscription_id as string).slice(0, 16)
            : (row.id as string).slice(0, 8).toUpperCase()}
        </span>
      ),
    },
    {
      key: "customer",
      label: "Customer",
      render: (row) => {
        const profile = row.profile as Record<string, unknown> | undefined;
        return (
          <div>
            <p className="font-medium text-gray-900 text-sm">
              <span className="truncate max-w-[160px] block">
                {(profile?.full_name as string) ?? "—"}
              </span>
            </p>
            <p className="text-xs text-gray-400">
              <span className="truncate max-w-[160px] block">
                {(profile?.email as string) ?? "—"}
              </span>
            </p>
          </div>
        );
      },
    },
    {
      key: "plan",
      label: "Plan",
      render: (row) => (
        <Badge variant="outline" className="text-xs whitespace-nowrap">
          {getPlanLabel((row.price_id as string) ?? "")}
        </Badge>
      ),
    },
    {
      key: "created_at",
      label: "Date",
      sortable: true,
      render: (row) => (
        <span className="text-sm tabular-nums whitespace-nowrap">
          {row.created_at ? new Date(row.created_at as string).toLocaleDateString("en-CA") : "—"}
        </span>
      ),
    },
    {
      key: "amount",
      label: "Amount (CA$)",
      render: (row) => {
        const key = getPlanKey((row.price_id as string) ?? "");
        const price = PLAN_PRICES[key];
        return (
          <span className="tabular-nums font-medium text-sm whitespace-nowrap">
            {price != null ? `CA$${price.toFixed(2)}` : "—"}
          </span>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span className="whitespace-nowrap">
          <StatusBadge status={row.status as string} />
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <div className="flex items-center gap-1">
          {row.stripe_subscription_id && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs whitespace-nowrap"
              onClick={() =>
                window.open(
                  `https://dashboard.stripe.com/subscriptions/${row.stripe_subscription_id}`,
                  "_blank"
                )
              }
            >
              View PDF
            </Button>
          )}
          {row.status === "past_due" && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs whitespace-nowrap"
              onClick={() => markPaidMutation.mutate(row.id as string)}
              disabled={markPaidMutation.isPending}
            >
              Mark Paid
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-amber-700 hover:text-amber-800 whitespace-nowrap"
            onClick={() => {
              setRefundTarget(row);
              setRefundReason("");
            }}
          >
            Refund
          </Button>
        </div>
      ),
    },
  ];

  const filters = (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="h-9 w-[130px] text-sm">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="active">Paid</SelectItem>
          <SelectItem value="past_due">Past Due</SelectItem>
          <SelectItem value="canceled">Void</SelectItem>
        </SelectContent>
      </Select>
      <Select value={planFilter} onValueChange={setPlanFilter}>
        <SelectTrigger className="h-9 w-[160px] text-sm">
          <SelectValue placeholder="All Plans" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Plans</SelectItem>
          <SelectItem value="individual">Individual</SelectItem>
          <SelectItem value="strata">Strata Council</SelectItem>
          <SelectItem value="property_manager">Property Manager</SelectItem>
        </SelectContent>
      </Select>
      <Input
        type="date"
        value={dateFrom}
        onChange={(e) => setDateFrom(e.target.value)}
        className="h-9 w-[140px] text-sm"
        aria-label="From date"
      />
      <Input
        type="date"
        value={dateTo}
        onChange={(e) => setDateTo(e.target.value)}
        className="h-9 w-[140px] text-sm"
        aria-label="To date"
      />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          title="Total Invoiced (This Month)"
          value={`CA$${totalInvoiced.toFixed(2)}`}
          icon={<Receipt className="h-5 w-5" />}
          color="blue"
          loading={invoicesQuery.isLoading}
        />
        <StatCard
          title="Total Collected"
          value={`CA$${totalCollected.toFixed(2)}`}
          icon={<CheckCircle2 className="h-5 w-5" />}
          color="green"
          loading={invoicesQuery.isLoading}
        />
        <StatCard
          title="Outstanding"
          value={`CA$${outstanding.toFixed(2)}`}
          icon={<Clock className="h-5 w-5" />}
          color={outstanding > 0 ? "red" : "blue"}
          loading={invoicesQuery.isLoading}
        />
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={filtered}
        loading={invoicesQuery.isLoading}
        filters={filters}
        emptyMessage="No invoices found."
        searchPlaceholder="Search customer or invoice…"
        onSearch={() => {}}
      />

      {/* Refund Modal */}
      <ConfirmModal
        open={!!refundTarget}
        onClose={() => setRefundTarget(null)}
        onConfirm={() => {
          if (refundTarget)
            refundMutation.mutate({
              customerId: (refundTarget.stripe_customer_id as string) ?? (refundTarget.user_id as string),
              reason: refundReason,
            });
        }}
        title="Refund Payment"
        description={`Refund CA$${PLAN_PRICES[getPlanKey((refundTarget?.price_id as string) ?? "")] != null ? PLAN_PRICES[getPlanKey((refundTarget?.price_id as string) ?? "")].toFixed(2) : "—"} for ${(refundTarget?.profile as Record<string, unknown> | undefined)?.full_name as string ?? "customer"}. This action cannot be undone.`}
        requireTyping
        loading={refundMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
